import { type Accessor, createSignal, onCleanup } from "solid-js";
import { BACKGROUND_RELOAD_HEADER, router } from ".";

// A visit is ours when it targets the page we are on, which means comparing paths
// rather than full URLs. `window.location` still holds the previous query string when
// a visit starts, so matching on href misses every param change — a date range, a
// filter — and the loading state never shows for exactly the visits that replace our data.
const isSamePath = (url1: URL | Location, url2: URL | Location): boolean => {
  const a = new URL(url1.href);
  const b = new URL(url2.href);

  return a.origin === b.origin && a.pathname === b.pathname;
};

/** True while a visit to this page is re-fetching any of `keys` (a full visit counts too).
 *
 * Visits are held by the visit object Inertia fires with BOTH `start` and `finish`, so
 * overlapping visits (a poll landing mid-drawer-close) each release only their own hold.
 * Waiting on the visit, not on the prop's value changing, is the point: a reload can answer
 * with the value already on the page, and props are applied with `reconcile` (see App.ts),
 * which notifies nothing for an equal value.
 *
 * A background refresh (backgroundReload) re-fetches data the page is already showing,
 * so it never counts as reloading. */
export function createReloading(keys: Accessor<string[]>): Accessor<boolean> {
  const [reloading, setReloading] = createSignal<readonly object[]>([]);

  const stopListening = [
    router.on("start", (e) => {
      const visit = e.detail.visit;
      if (visit.headers[BACKGROUND_RELOAD_HEADER]) return;
      const isPartialVisit = visit.only.length > 0 || visit.except.length > 0;
      const isReloadingKey = visit.only.some((key) => keys().includes(key));

      if (isSamePath(visit.url, window.location) && (!isPartialVisit || isReloadingKey)) {
        setReloading((visits) => [...visits, visit]);
      }
    }),
    // Fires after the response's page swap has been applied, and exactly once per
    // request — including cancelled and interrupted ones, which must not strand the state.
    router.on("finish", (e) => {
      setReloading((visits) => visits.filter((visit) => visit !== e.detail.visit));
    }),
  ];

  onCleanup(() => stopListening.forEach((stop) => stop()));

  return () => reloading().length > 0;
}
